import window from 'window-or-global';
import { bind, unbind } from '@confluence-classic/confluence-event_DO_NOT_USE';
import { inlineTasks } from '../utils/features';
import { getOverride } from '@confluence/meta';

// [] autocomplete macro to trigger inline Task
const REGEX_SQUARE_OPEN_SQUARE_BRACKET = /^\u200b?(\[)$/;

function getListFromSelection(editor) {
	return editor.dom.getParent(editor.selection.getStart(), 'UL,OL');
}

function isTasklist(node) {
	return !!(
		node &&
		node.nodeName.toLowerCase() === 'ul' &&
		node.classList.contains('inline-task-list')
	);
}

function isUnorderedList(node) {
	return node && node.nodeName.toLowerCase() === 'ul' && !isTasklist(node);
}

function removeDecorations(list) {
	// remove the Attrs/Classes we added to the UL to make it a tasklist, note list can also be OL
	if (list && list.classList.contains('inline-task-list')) {
		list.classList.remove('inline-task-list');
		list.removeAttribute('data-inline-tasks-content-id');
		// convert HTMLCollection to Array
		const items = Array.prototype.slice.call(list.children);
		items.forEach((li) => {
			li.removeAttribute('data-inline-task-id');
			li.classList.remove('checked');
		});
	}
}

function applyTasklist(editor) {
	const id = editor.dom.uniqueId();
	editor.execCommand('InsertUnorderedList', false, {
		'list-attributes': {
			'data-task-list-id': id,
			class: 'inline-task-list',
		},

		'list-item-attributes': {
			'data-inline-task-id': '',
		},
	});

	editor.dom.select(`[data-task-list-id="${id}"]`).forEach((elm) => {
		const li = elm.querySelectorAll('li')[0];
		if (li && editor.dom.isEmpty(elm)) {
			li.innerHTML = `<span class='placeholder-inline-tasks'>${editor.translate(
				'tinymce.confluence.placeholder.inline.tasks',
			)}</span>`;
			editor.selection.select(li.firstChild, true);
		}
		elm.removeAttribute('data-task-list-id');
	});
}

export default function taskList() {
	function plugin(editor) {
		const isAllTaskList = (list) => {
			const items = [...list.childNodes];
			return items.every((li) => {
				return li.hasAttribute('data-inline-task-id');
			});
		};

		const isIndentTaskListCandidate = (list) => {
			// if the selection is on a List item and that list item has a parent who is a UL.inline-task-list
			// then the user has tried to indent a list that is a candidate for a new task list
			const parentList = editor.dom.getParent(list.parentElement, 'UL');
			return (
				list &&
				list.tagName === 'UL' &&
				parentList &&
				parentList.classList.contains('inline-task-list') &&
				list != parentList
			);
		};

		const decorateTasklist = () => {
			const list = getListFromSelection(editor);

			if (list && isIndentTaskListCandidate(list) && isAllTaskList(list)) {
				editor.undoManager.ignore(() => {
					list.classList.add('inline-task-list');
				});
			}
		};

		function getCurrentListItem() {
			const el = editor.selection.getStart();

			// Get start will return BR if the LI only contains a BR or an empty element as we use these to fix caret position
			const li =
				el && el.tagName && el.tagName.toUpperCase() === 'LI' ? el : editor.dom.getParent(el, 'li');

			return li;
		}

		function handleNewListItem(e) {
			if (e.keyCode === window.tinymce.util.VK.ENTER && !e.shiftKey) {
				const li = getCurrentListItem();
				if (li) {
					editor.dom.removeClass(li, 'checked');
					if (editor.dom.hasClass(li.parentNode, 'inline-task-list')) {
						// Set the id to empty, so that it can be recognized when pasting <li> elements.
						li.setAttribute('data-inline-task-id', '');
					} else {
						li.removeAttribute('data-inline-task-id');
					}
				}
			}
		}

		const inlineTaskList = () => {
			//TODO: revert this work around
			if (getOverride('content-type') === 'page' || getOverride('content-type') === 'blogpost') {
				editor.undoManager.transact(() => {
					if (isUnorderedList(getListFromSelection(editor))) {
						/*
                            work around for noToggle behaviour.
                            - when the OL button is invoked on a UL element, it will convert UL -> OL
                            - when the UL button is invoked on a UL element, it will toggle off UL -> no list
                            to emulate a noToggle behaviour we convert UL -> OL then applytasklist -> UL.inline-task-list
                        */
						editor.execCommand('InsertOrderedList');
						removeDecorations(getListFromSelection(editor));
					}
					applyTasklist(editor);
				});
			}
		};

		editor.addCommand('InsertInlineTaskList', inlineTaskList);

		editor.on('BeforeExecCommand', (ev) => {
			if (
				ev.command === 'InsertUnorderedList' &&
				isTasklist(getListFromSelection(editor)) &&
				!ev.value
			) {
				editor.undoManager.ignore(() => {
					editor.execCommand('InsertOrderedList');
					removeDecorations(getListFromSelection(editor));
				});
			} else if (ev.command === 'InsertOrderedList' && !ev.value) {
				editor.undoManager.ignore(() => {
					removeDecorations(getListFromSelection(editor));
				});
			}
		});

		editor.on('execCommand', (ev) => {
			if (ev.command === 'Indent') {
				decorateTasklist();
			}
		});

		/* This is very dependant on the images used and the padding, etc */
		function isInCheckArea(e, el) {
			let offsetX, offsetY;

			// Browsers are all over the place for mouse events. :/
			if (window.tinymce.Env.gecko) {
				const clientRect = el.getBoundingClientRect();
				// We add the current scrolling position to clientRect because its values are relative to the viewport
				offsetX = e.pageX - (clientRect.left + window.tinymce.DOM.getViewPort(editor.getWin()).x);
				offsetY = e.pageY - (clientRect.top + window.tinymce.DOM.getViewPort(editor.getWin()).y);
			} else {
				offsetX = e.offsetX;
				offsetY = e.offsetY;
			}

			return offsetX >= 3 && offsetX <= 14 && offsetY >= 3 && offsetY <= 14;
		}

		function handleTaskListCheck(e) {
			const el = e.target;

			if (el.tagName !== 'LI') {
				return;
			}

			const list = editor.dom.getParent(el, 'ul,ol');

			if (list.tagName === 'UL' && editor.dom.hasClass(list, 'inline-task-list')) {
				if (isInCheckArea(e, el)) {
					if (editor.dom.hasClass(el, 'checked')) {
						editor.dom.removeClass(el, 'checked');
					} else {
						editor.dom.addClass(el, 'checked');
					}
				}
			}
		}

		editor.on('Click', handleTaskListCheck);
		editor.on('KeyUp', handleNewListItem);
		editor.on('KeyDown', (e) => {
			// Check for tab but not ctrl/cmd+tab since it switches browser tabs, not shift because thats outdent
			if (
				e.shiftKey ||
				e.keyCode !== window.tinymce.util.VK.TAB ||
				window.tinymce.util.VK.metaKeyPressed(e)
			) {
				return;
			}

			decorateTasklist();
		});

		function autoformatHandler(e, data) {
			//TODO: revert this work around
			if (
				!inlineTasks() &&
				getOverride('content-type') !== 'page' &&
				getOverride('content-type') !== 'blogpost'
			) {
				//disable inline task inside comment
				return;
			}
			const handlerManager = data.handlerManager;
			const createHandler = data.createHandler;
			handlerManager.registerHandler(
				']'.charCodeAt(0),
				createHandler(
					REGEX_SQUARE_OPEN_SQUARE_BRACKET,
					() => {
						editor.execCommand('InsertInlineTaskList');
					},
					true,
				),
			);
		}
		bind('confluence.editor.registerHandlers', autoformatHandler);

		function rteReadyHandler() {
			// Add new custom query commands, when the cursor is in the a task list, the toolbar button state should be active
			editor.addQueryStateHandler('InsertInlineTaskList', () => {
				return isTasklist(getListFromSelection(editor));
			});

			// In order to override the native InsertUnorderedList QueryCommand, we declare the definition after an Editor instance is created
			// Override the default behaviour, if the cursor is inside a <ul> that does not contain the .inline-task-list class then its an unordered list
			editor.addQueryStateHandler('InsertUnorderedList', () => {
				return isUnorderedList(getListFromSelection(editor));
			});
		}
		bind('rte-ready', rteReadyHandler);

		//clean up event handler to avoid ref to removed editor
		editor.on('Remove', () => {
			unbind('confluence.editor.registerHandlers', autoformatHandler);
			unbind('rte-ready', rteReadyHandler);
		});
	}

	window.tinymce.PluginManager.add('tasklist', plugin);
}
